/**
 * Telemetry orchestrator.
 *
 * Describes the full event catalogue as a discriminated union, enriches every
 * event with app/environment context, and fans the payload out to every
 * configured sink (PostHog, Datadog RUM, optional HTTP beacon).
 *
 * Design notes:
 *   - Events are typed. The union is the catalogue; there is no string-soup.
 *   - Wire names are PascalCase with a CommunityGate prefix so they don't
 *     collide with the main app's event namespace in PostHog.
 *   - Context keys are merged into every event at dispatch time; call sites
 *     don't have to remember them.
 *   - Any sink that wasn't configured silently no-ops (see each sink's init).
 *   - A master VITE_ENABLE_ANALYTICS flag kills all tracking at once — handy
 *     for local dev or quick hotfixes.
 */
import { capturePostHog, identifyPostHog, initPostHog, resetPostHog } from "./posthog";
import { captureDatadogAction, captureDatadogError, initDatadog } from "./datadog";

// ---------------------------------------------------------------------------
// Event catalogue
// ---------------------------------------------------------------------------

export type TelemetryEvent =
  | { type: "page_loaded"; eventId: string }
  | { type: "session_resumed"; eventId: string }
  | { type: "session_expired"; eventId: string }
  | { type: "otp_send_requested"; eventId: string }
  | { type: "otp_sent"; eventId: string }
  | { type: "otp_send_failed"; eventId: string; reason: string }
  | { type: "otp_submitted"; eventId: string; attemptsRemaining: number }
  | { type: "otp_validated"; eventId: string }
  | { type: "otp_invalid"; eventId: string; attemptsRemaining: number }
  | { type: "otp_locked"; eventId: string }
  | { type: "otp_resent"; eventId: string }
  | { type: "whatsapp_changed"; eventId: string }
  | { type: "event_fetched"; eventId: string; status: string; isFull: boolean }
  | { type: "event_fetch_failed"; eventId: string; reason: string }
  | { type: "join_requested"; eventId: string }
  | { type: "join_capacity_refetch"; eventId: string }
  | { type: "join_succeeded"; eventId: string; attendanceKind: "confirmed" | "walked_in" }
  | { type: "join_failed"; eventId: string; reason: string }
  | {
      type: "meeting_entered";
      eventId: string;
      attendanceKind: "confirmed" | "walked_in";
    }
  | { type: "open_app_clicked"; eventId: string; source: "preview" | "error" }
  | { type: "support_clicked"; eventId: string }
  | { type: "error_retried"; eventId: string; errorCode: string }
  | { type: "error_closed"; eventId: string }
  | {
      type: "mobile_redirect";
      path: string;
      deeplink: string;
      storeFallback: string;
      // Null when the visitor landed somewhere other than /events/:id.
      eventId: string | null;
    };

// Wire names. The mapped-type constraint guarantees a compile error if a
// TelemetryEvent variant is added without updating the map.
const EVENT_NAMES: Record<TelemetryEvent["type"], string> = {
  page_loaded: "CommunityGatePageLoaded",
  session_resumed: "CommunityGateSessionResumed",
  session_expired: "CommunityGateSessionExpired",
  otp_send_requested: "CommunityGateOtpSendRequested",
  otp_sent: "CommunityGateOtpSent",
  otp_send_failed: "CommunityGateOtpSendFailed",
  otp_submitted: "CommunityGateOtpSubmitted",
  otp_validated: "CommunityGateOtpValidated",
  otp_invalid: "CommunityGateOtpInvalid",
  otp_locked: "CommunityGateOtpLocked",
  otp_resent: "CommunityGateOtpResent",
  whatsapp_changed: "CommunityGateWhatsappChanged",
  event_fetched: "CommunityGateEventFetched",
  event_fetch_failed: "CommunityGateEventFetchFailed",
  join_requested: "CommunityGateJoinRequested",
  join_capacity_refetch: "CommunityGateJoinCapacityRefetch",
  join_succeeded: "CommunityGateJoinSucceeded",
  join_failed: "CommunityGateJoinFailed",
  meeting_entered: "CommunityGateMeetingEntered",
  open_app_clicked: "CommunityGateOpenAppClicked",
  support_clicked: "CommunityGateSupportClicked",
  error_retried: "CommunityGateErrorRetried",
  error_closed: "CommunityGateErrorClosed",
  mobile_redirect: "CommunityGateMobileRedirect",
};

// Events whose call site navigates the tab away immediately after tracking.
// The default transport gets cancelled by the unload, so these would be lost —
// which matters most for meeting_entered, the event that proxies attendance.
const PRE_NAVIGATION_EVENTS: ReadonlySet<TelemetryEvent["type"]> = new Set([
  "meeting_entered",
  "mobile_redirect",
  "open_app_clicked",
  "support_clicked",
]);

// ---------------------------------------------------------------------------
// Context + config
// ---------------------------------------------------------------------------

interface Context {
  app: "community-gate";
  appVersion: string;
  environment: string;
}

const CONTEXT: Context = {
  app: "community-gate",
  appVersion: __APP_VERSION__,
  environment: (import.meta.env.VITE_ENVIRONMENT ?? "development").trim(),
};

const ENABLED = (import.meta.env.VITE_ENABLE_ANALYTICS ?? "true").trim() !== "false";
const DEBUG = (import.meta.env.VITE_ANALYTICS_DEBUG ?? "").trim() === "true";
const BEACON_URL = (import.meta.env.VITE_TELEMETRY_URL ?? "").trim();

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initTelemetry(): void {
  if (!ENABLED) return;
  initDatadog();
  initPostHog();
}

export function track(event: TelemetryEvent): void {
  if (!ENABLED) return;

  const name = EVENT_NAMES[event.type];
  const properties = { ...CONTEXT, ...eventProperties(event) };

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.info("[telemetry]", name, properties);
  }

  capturePostHog(name, properties, {
    beforeNavigation: PRE_NAVIGATION_EVENTS.has(event.type),
  });
  captureDatadogAction(name, properties);
  sendBeacon(event);
}

/**
 * Bind the Beconfident user id to the session so every event tracked from here
 * on carries it. Call as soon as authentication resolves the user.
 *
 * This is what makes "which user attended which event" answerable: the events
 * already carry `eventId`, they just had nobody attached to them.
 */
export function identifyUser(userId: string): void {
  if (!ENABLED) return;
  identifyPostHog(userId);
}

/**
 * Clear the bound identity. Call when the session ends — a cleared token or an
 * explicit switch to another number — so the id doesn't carry over to whoever
 * uses the tab next.
 */
export function resetUser(): void {
  if (!ENABLED) return;
  resetPostHog();
}

/**
 * Annotate an exception with the current context and ship it to Datadog.
 * Unhandled errors are already auto-captured by RUM; use this for errors you
 * want to tag with the call-site's intent.
 */
export function trackError(error: unknown, context?: Record<string, unknown>): void {
  if (!ENABLED) return;
  captureDatadogError(error, { ...CONTEXT, ...context });
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

function eventProperties(event: TelemetryEvent): Record<string, unknown> {
  const { type: _type, ...rest } = event;
  void _type;
  return rest;
}

function sendBeacon(event: TelemetryEvent): void {
  if (!BEACON_URL) return;
  const payload = JSON.stringify({
    ...event,
    ...CONTEXT,
    timestamp: new Date().toISOString(),
  });
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(BEACON_URL, blob);
      return;
    }
    void fetch(BEACON_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* swallow: telemetry must never break the flow */
    });
  } catch {
    /* swallow */
  }
}
